/* eslint-disable react-native/no-color-literals */
import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
  FlatList,
  ActivityIndicator,
  TextInput,
} from "react-native";
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
// eslint-disable-next-line import/default
import BarcodeScanner from "react-native-scan-barcode";
import { check, PERMISSIONS, RESULTS, request } from "react-native-permissions";
import Text from "@components/CustomText";
import Cross from "@assets/svg/CrossIcon";
import Lock from "@assets/svg/Lock";
import Camera from "@assets/svg/Camera";
import SkeletonLoaderList from "@components/SkeletonLoaderList";
import colors from "@constants/color";
// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import toasts from "@utils/toast";
import { getPickShipmentsV3, pickTpV3 } from "@services/api";
import {
  isNativeCodeScannerAvailable,
  normalizeScannerConfig,
  scanCode,
} from "@utils/nativeCodeScanner";
import PickTpManualConfirmModal from "./PickTpManualConfirmModal";
import {
  PickTpRack,
  PickTpShipment,
  PickTpMeta,
  PickTpShipmentPayloadEntry,
  PickTpScannerConfigs,
  ShipmentScanState,
  ScanKind,
  resolveScanValue,
  buildScanExpectations,
  isAllShipmentsScanned,
  scannedCount,
  buildPickTpShipmentsPayload,
  buildShipmentsPayloadEntries,
  isRackLocked,
  pendingShipmentsForRack,
  shipmentsForRack,
  getPickTpErrorMessage,
} from "./pickTpHelpers";

const PATH = "pickTp";

interface Props {
  visible: boolean;
  meta: PickTpMeta | null;
  onClose: () => void;
  onSubmitSuccess: () => void;
  onFallbackToItems: (carriedShipments: PickTpShipmentPayloadEntry[]) => void;
}

const ItemSeparator = () => <View style={separatorStyle} />;
const separatorStyle = {
  height: StyleSheet.hairlineWidth,
  backgroundColor: colors.black02,
};

const PickTpShipmentsModalContent: React.FC<Props> = ({
  meta,
  onClose,
  onSubmitSuccess,
  onFallbackToItems,
}) => {
  // Insets from the SafeAreaProvider nested inside the Modal (wrapper below),
  // matching PickupConfirmationModal.tsx's pattern — the root provider
  // reports bottom: 0 while a navigationBarTranslucent modal is open.
  const insets = useSafeAreaInsets();

  const [loading, setLoading] = useState(true);
  const [racks, setRacks] = useState<PickTpRack[]>([]);
  const [shipments, setShipments] = useState<PickTpShipment[]>([]);
  const [total, setTotal] = useState(0);
  const [scanState, setScanState] = useState<ShipmentScanState>({});
  const [activeRackId, setActiveRackId] = useState<number | null>(null);
  const [scanInputValue, setScanInputValue] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [scannerConfigs, setScannerConfigs] = useState<PickTpScannerConfigs>(
    {},
  );
  const [showManualConfirm, setShowManualConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchShipments = useCallback(async () => {
    if (!meta) return;
    setLoading(true);
    try {
      const { data } = await getPickShipmentsV3({
        cx_user_id: meta.cx_user_id,
        order_ids: meta.order_ids,
      });
      setRacks(Array.isArray(data?.racks) ? data.racks : []);
      setShipments(Array.isArray(data?.shipments) ? data.shipments : []);
      setTotal(Number(data?.total) || 0);
      setScannerConfigs(data?.scanner_config || {});
    } catch {
      toasts.showShortToast(t(`${PATH}/loadError`));
    } finally {
      setLoading(false);
    }
  }, [meta]);

  useEffect(() => {
    setScanState({});
    setActiveRackId(null);
    setScanInputValue("");
    setShowScanner(false);
    setShowManualConfirm(false);
    fetchShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.cx_user_id]);

  // `scanKind` records how the box was actually accounted for: only a camera
  // decode is SCANNED. A box id typed into the input is a manual entry and is
  // logged as one — cmo_shipment_pick_logs.scan_kind is the only record of which
  // boxes the DB physically had in hand when a missing-item claim is disputed,
  // so a typed value must not launder itself as a scan.
  const applyResolution = (rawValue: string, scanKind: ScanKind) => {
    const resolution = resolveScanValue({
      value: rawValue,
      racks,
      shipments,
      scanState,
      activeRackId,
    });

    switch (resolution.type) {
      case "shipment_scanned":
        setScanState((prev) => ({
          ...prev,
          [resolution.shipment.shipment_id]: {
            scan_kind: scanKind,
          },
        }));
        setScanInputValue("");
        return;
      case "rack_unlocked":
        setActiveRackId(resolution.rack.rack_id);
        setScanInputValue("");
        return;
      case "already_done":
        toasts.showShortToast(t(`${PATH}/scanErrorAlreadyDone`));
        setScanInputValue("");
        return;
      case "wrong_rack":
        toasts.showShortToast(t(`${PATH}/scanErrorWrongRack`));
        setScanInputValue("");
        return;
      case "unknown":
        toasts.showShortToast(t(`${PATH}/scanErrorUnknown`));
        return;
      case "empty":
      default:
        return;
    }
  };

  const ensureCameraPermission = async () => {
    try {
      const result = await check(PERMISSIONS.ANDROID.CAMERA);
      if (result !== RESULTS.GRANTED) {
        await request(PERMISSIONS.ANDROID.CAMERA);
      }
    } catch {
      // falls back to manual entry — no-op.
    }
  };

  // Native scanner (CodeScannerModule) when the binary has it: full screen,
  // ML Kit, QR | Barcode switcher unless the API pinned a mode for this step.
  // Older APKs (JS CodePushed past the native release) and a camera that
  // fails to start keep the in-sheet react-native-scan-barcode view.
  const openNativeScanner = async () => {
    const config = normalizeScannerConfig(
      activeRackHasPending ? scannerConfigs.shipment : scannerConfigs.rack,
    );
    // The scanner stays open on a wrong label and only returns a code that
    // moves the sheet forward, so a stray scan doesn't cost the DB a re-tap.
    const { accept, reject } = buildScanExpectations({
      racks,
      shipments,
      scanState,
      activeRackId,
    });
    const rejectHints: Record<string, string> = {};
    Object.entries(reject).forEach(([value, type]) => {
      rejectHints[value] = t(
        `${PATH}/${type === "already_done" ? "scanErrorAlreadyDone" : "scanErrorWrongRack"}`,
      );
    });
    try {
      const result = await scanCode({
        ...config,
        title:
          config.title ||
          (activeRackHasPending
            ? t(`${PATH}/scannerTitleShipment`)
            : t(`${PATH}/scannerTitleRack`)),
        accept_values: accept,
        reject_hints: rejectHints,
        invalid_text: t(`${PATH}/scanErrorUnknown`),
      });
      if (!result.didCancel && result.value) {
        applyResolution(result.value, "SCANNED");
      }
    } catch {
      setShowScanner(true);
    }
  };

  const toggleScanner = async () => {
    if (!showScanner) {
      await ensureCameraPermission();
      if (isNativeCodeScannerAvailable()) {
        await openNativeScanner();
        return;
      }
    }
    setShowScanner((prev) => !prev);
  };

  // Fires continuously while mounted — unmount on first decode so a re-fire
  // can't double-post (same debounce pattern as SortingDeliveries/CrateScanner.js).
  const onBarCodeRead = ({ data }: { type: string; data: string }) => {
    setShowScanner(false);
    if (data) applyResolution(data, "SCANNED");
  };

  const handleManualTextSubmit = () => {
    if (!scanInputValue.trim()) return;
    applyResolution(scanInputValue, "MANUAL");
  };

  const handleManualConfirmSubmit = (shipmentIds: string[]) => {
    setScanState((prev) => {
      const next = { ...prev };
      shipmentIds.forEach((id) => {
        next[id] = { scan_kind: "MANUAL" as ScanKind };
      });
      return next;
    });
    setShowManualConfirm(false);
  };

  const handleFallback = () => {
    onFallbackToItems(buildShipmentsPayloadEntries(shipments, scanState));
  };

  const allScanned = isAllShipmentsScanned(shipments, scanState);

  const handleSubmit = async () => {
    if (!meta) return;
    if (!allScanned) {
      toasts.showShortToast(t(`${PATH}/submitIncomplete`));
      return;
    }
    setSubmitting(true);
    try {
      await pickTpV3(
        buildPickTpShipmentsPayload({ meta, shipments, scanState }),
      );
      onSubmitSuccess();
    } catch (err) {
      toasts.showShortToast(getPickTpErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const scannedSoFar = scannedCount(shipments, scanState);
  const activeRackHasPending =
    activeRackId != null &&
    pendingShipmentsForRack(shipments, activeRackId, scanState).length > 0;

  const renderShipmentRow = (
    shipment: PickTpShipment,
    isNextInActiveRack: boolean,
  ) => {
    const scan = scanState[shipment.shipment_id];
    let statusLabel = t(`${PATH}/statusPending`);
    let statusStyle = styles.statusPending;
    if (scan?.scan_kind === "SCANNED") {
      statusLabel = t(`${PATH}/statusScanned`);
      statusStyle = styles.statusScanned;
    } else if (scan?.scan_kind === "MANUAL") {
      statusLabel = t(`${PATH}/statusManual`);
      statusStyle = styles.statusManual;
    } else if (isNextInActiveRack) {
      statusLabel = t(`${PATH}/statusScanNow`);
      statusStyle = styles.statusScanNow;
    }

    return (
      <View
        key={shipment.shipment_id}
        style={styles.shipmentRow}
        testID={`pick-tp-shipment-${shipment.shipment_id}`}
      >
        <Text variant="body_text_4" style={styles.shipmentId}>
          {shipment.shipment_id}
        </Text>
        <View style={[styles.statusPill, statusStyle]}>
          <Text variant="caption_3" style={styles.statusText}>
            {statusLabel}
          </Text>
        </View>
      </View>
    );
  };

  const renderRack = ({ item: rack }: { item: PickTpRack }) => {
    const locked = isRackLocked({
      rack,
      shipments,
      scanState,
      activeRackId,
    });
    const rackShipments = shipmentsForRack(shipments, rack.rack_id);
    const pending = pendingShipmentsForRack(shipments, rack.rack_id, scanState);
    const nextShipmentId =
      rack.rack_id === activeRackId && pending.length > 0
        ? pending[0].shipment_id
        : null;

    return (
      <View style={styles.rackBlock} testID={`pick-tp-rack-${rack.rack_id}`}>
        <View style={styles.rackHeader}>
          {locked ? <Lock width={16} height={16} /> : null}
          <Text
            variant="body_text_2"
            style={[styles.rackTitle, locked && styles.rackTitleLocked]}
          >
            {t(`${PATH}/rackRow`, {
              rack_no: rack.rack_no,
              rack_name: rack.rack_name,
              count: `${rack.shipment_count}`,
            })}
          </Text>
          {locked ? (
            <Text variant="caption_2" style={styles.scanBtnLabel}>
              {t(`${PATH}/scanBtn`)}
            </Text>
          ) : null}
        </View>
        {!locked &&
          rackShipments.map((shipment) =>
            renderShipmentRow(
              shipment,
              shipment.shipment_id === nextShipmentId,
            ),
          )}
      </View>
    );
  };

  return (
    <>
      <View style={styles.overlay} />

      <View style={[styles.sheet, { paddingBottom: insets.bottom }]}>
        <Pressable
          style={styles.closeBtn}
          onPress={onClose}
          testID="pick-tp-cancel-btn-x"
        >
          <Cross width={14} height={14} />
        </Pressable>

        <View style={styles.header}>
          <Text variant="title_20">{t(`${PATH}/sheetTitle`)}</Text>
          {!loading && (
            <Text variant="caption_2" style={styles.headerCount}>
              {t(`${PATH}/scannedCounter`, {
                scanned: `${scannedSoFar}`,
                total: `${total}`,
              })}
            </Text>
          )}
        </View>

        <View style={styles.divider} />

        {loading ? (
          <View style={styles.skeletonWrapper}>
            <SkeletonLoaderList isLoading length={4} />
          </View>
        ) : (
          <>
            <Text variant="caption_2" style={styles.sectionTitle}>
              {t(`${PATH}/racksSectionTitle`)}
            </Text>
            <FlatList
              data={racks}
              keyExtractor={(rack) => `${rack.rack_id}`}
              renderItem={renderRack}
              ItemSeparatorComponent={ItemSeparator}
              showsVerticalScrollIndicator={false}
              ListEmptyComponent={
                <View style={styles.emptyState}>
                  <Text variant="body_text_3" style={styles.emptyText}>
                    {t(`${PATH}/emptyState`)}
                  </Text>
                </View>
              }
              contentContainerStyle={styles.listContent}
            />

            <View style={styles.scanInputRow}>
              <TextInput
                style={styles.scanInput}
                placeholder={
                  activeRackHasPending
                    ? t(`${PATH}/inputPlaceholderShipment`)
                    : t(`${PATH}/inputPlaceholderRack`)
                }
                placeholderTextColor={colors.mediumBlacklightBlack}
                value={scanInputValue}
                onChangeText={setScanInputValue}
                onSubmitEditing={handleManualTextSubmit}
                autoCapitalize="characters"
                autoCorrect={false}
                testID="pick-tp-scan-input"
              />
              {scanInputValue.trim().length > 0 ? (
                <TouchableOpacity
                  style={styles.scanIconBtn}
                  onPress={handleManualTextSubmit}
                  testID="pick-tp-scan-submit-btn"
                >
                  <Text variant="body_text_2" style={styles.scanSubmitIcon}>
                    {"✓"}
                  </Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.scanIconBtn}
                  onPress={toggleScanner}
                  testID="pick-tp-scan-toggle-btn"
                >
                  <Camera width={18} height={17} />
                </TouchableOpacity>
              )}
            </View>

            {!activeRackHasPending && (
              <Text variant="caption_3" style={styles.lockedHint}>
                {t(`${PATH}/lockedHint`)}
              </Text>
            )}

            {showScanner && (
              <View style={styles.scannerWrapper}>
                <BarcodeScanner
                  onBarCodeRead={onBarCodeRead}
                  style={styles.scanner}
                  cameraType="back"
                />
              </View>
            )}

            {activeRackId != null && (
              <Pressable
                style={styles.manualLinkWrapper}
                onPress={() => setShowManualConfirm(true)}
                testID="pick-tp-manual-link"
              >
                <Text variant="body_text_3" style={styles.manualLinkText}>
                  {t(`${PATH}/manualLink`)}
                </Text>
              </Pressable>
            )}

            <Pressable
              style={styles.fallbackRow}
              onPress={handleFallback}
              testID="pick-tp-fallback-row"
            >
              <Text variant="body_text_3" style={styles.fallbackText}>
                {t(`${PATH}/fallbackRow`)}
              </Text>
            </Pressable>
          </>
        )}

        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.footerBtn, styles.cancelBtn]}
            onPress={onClose}
            disabled={submitting}
            activeOpacity={0.7}
            testID="pick-tp-cancel-btn"
          >
            <Text variant="body_text_2" style={styles.cancelText}>
              {t(`${PATH}/cancel`)}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.footerBtn,
              styles.submitBtn,
              (loading || !allScanned) && styles.submitBtnDisabled,
            ]}
            onPress={handleSubmit}
            disabled={loading || submitting || !allScanned}
            activeOpacity={0.7}
            testID="pick-tp-submit-btn"
          >
            {submitting ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <Text variant="body_text_2" style={styles.submitText}>
                {t(`${PATH}/submit`)}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </View>

      <PickTpManualConfirmModal
        visible={showManualConfirm}
        pendingShipments={
          activeRackId != null
            ? pendingShipmentsForRack(shipments, activeRackId, scanState)
            : []
        }
        scannedCount={scannedSoFar}
        total={total}
        isSubmitting={false}
        onCancel={() => setShowManualConfirm(false)}
        onConfirm={handleManualConfirmSubmit}
      />
    </>
  );
};

const PickTpShipmentsModal: React.FC<Props> = (props) => {
  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={props.visible}
      onRequestClose={() => {}}
      statusBarTranslucent
      navigationBarTranslucent
    >
      {/* Nested provider measures the modal's own translucent window so
          useSafeAreaInsets() reports the real nav-bar inset inside the Modal
          (same pattern as PickupConfirmationModal.tsx / BagsItemsModal.tsx). */}
      <SafeAreaProvider>
        <PickTpShipmentsModalContent {...props} />
      </SafeAreaProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  sheet: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    backgroundColor: colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: "85%",
    paddingHorizontal: 16,
  },
  closeBtn: {
    position: "absolute",
    right: 10,
    top: -34,
    backgroundColor: colors.white,
    borderRadius: 50,
    padding: 8,
    zIndex: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 20,
    paddingBottom: 12,
  },
  headerCount: {
    color: colors.plain_green,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.black02,
  },
  skeletonWrapper: {
    paddingVertical: 16,
  },
  sectionTitle: {
    color: colors.mediumBlacklightBlack,
    marginTop: 14,
    marginBottom: 6,
  },
  listContent: {
    paddingBottom: 4,
  },
  emptyState: {
    paddingVertical: 24,
    alignItems: "center",
  },
  emptyText: {
    color: colors.mediumBlacklightBlack,
  },
  rackBlock: {
    paddingVertical: 10,
  },
  rackHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  rackTitle: {
    color: colors.mediumBlack,
    flex: 1,
  },
  rackTitleLocked: {
    color: colors.mediumBlacklightBlack,
  },
  scanBtnLabel: {
    color: colors.plain_green,
    fontWeight: "700",
  },
  shipmentRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    paddingLeft: 22,
  },
  shipmentId: {
    color: colors.mediumBlack,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  statusText: {
    fontWeight: "700",
  },
  statusScanned: {
    backgroundColor: "#E7F8EF",
  },
  statusManual: {
    backgroundColor: "#EAF1FF",
  },
  statusScanNow: {
    backgroundColor: colors.lightYellow,
  },
  statusPending: {
    backgroundColor: colors.primaryBg,
  },
  scanInputRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
    gap: 8,
  },
  scanInput: {
    flex: 1,
    height: 42,
    borderWidth: 1,
    borderColor: colors.lightMediumBlack,
    borderRadius: 8,
    paddingHorizontal: 12,
    color: colors.black,
  },
  scanIconBtn: {
    width: 42,
    height: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.lightMediumBlack,
    alignItems: "center",
    justifyContent: "center",
  },
  scanSubmitIcon: {
    color: colors.plain_green,
    fontWeight: "700",
  },
  lockedHint: {
    color: colors.mediumBlacklightBlack,
    marginTop: 6,
  },
  scannerWrapper: {
    height: 180,
    marginTop: 10,
    borderRadius: 8,
    overflow: "hidden",
  },
  scanner: {
    height: "100%",
    width: "100%",
  },
  manualLinkWrapper: {
    marginTop: 12,
  },
  manualLinkText: {
    color: colors.pacificBlue,
  },
  fallbackRow: {
    backgroundColor: colors.lightYellow,
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
  },
  fallbackText: {
    color: colors.plain_amber,
    fontWeight: "700",
  },
  footer: {
    flexDirection: "row",
    gap: 10,
    paddingVertical: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.black02,
  },
  footerBtn: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  cancelBtn: {
    borderWidth: 1,
    borderColor: colors.lightMediumBlack,
    backgroundColor: colors.white,
  },
  submitBtn: {
    backgroundColor: colors.plain_green,
  },
  submitBtnDisabled: {
    backgroundColor: colors.silver,
  },
  cancelText: {
    color: colors.black,
  },
  submitText: {
    color: colors.white,
  },
});

export default PickTpShipmentsModal;
